import { ApolloLink, FetchResult, NextLink, Observable, Operation } from 'apollo-link'
import type { IncomingMessage } from 'http'

// Dynamic import to avoid client-side bundling issues
let Sentry: typeof import('@sentry/node') | null = null
if (process.server) {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  Sentry = require('@sentry/node')
}

const IS_ON_PREM = process.env.ON_PREM === 'true'
const IS_DEVELOPMENT = process.env.NODE_ENV === 'development'
const ENABLE_SENTRY_DEV = process.env.ENABLE_SENTRY_DEV === 'true'
const ENABLE_PERFORMANCE_MONITORING = process.env.ENABLE_PERFORMANCE_MONITORING !== 'false'

// Keys to redact from variables
const SENSITIVE_KEYS = [
  'password',
  'pass',
  'token',
  'secret',
  'apikey',
  'authorization',
  'credential',
  'key',
  'session',
  'cookie',
  'jwt'
]

function sanitizeVariables(
  variables: Record<string, unknown> | undefined
): Record<string, unknown> {
  if (!variables) return {}

  const sanitized: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(variables)) {
    if (SENSITIVE_KEYS.some((sensitive) => key.toLowerCase().includes(sensitive))) {
      sanitized[key] = '[REDACTED]'
    } else if (Array.isArray(value)) {
      sanitized[key] = value.map((item) =>
        typeof item === 'object' && item !== null
          ? sanitizeVariables(item as Record<string, unknown>)
          : item
      )
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeVariables(value as Record<string, unknown>)
    } else {
      sanitized[key] = value
    }
  }
  return sanitized
}

function getOperationType(operation: Operation): string {
  const definition = operation.query.definitions?.[0]
  if (definition?.kind === 'OperationDefinition') {
    return (definition as { operation?: string }).operation || 'unknown'
  }
  return 'unknown'
}

function isEnabled(): boolean {
  if (IS_ON_PREM) return false
  if (IS_DEVELOPMENT && !ENABLE_SENTRY_DEV) return false
  if (!ENABLE_PERFORMANCE_MONITORING) return false
  return true
}

// Get span from request object (stored by ssrPerformance middleware)
function getSpanFromRequest(
  req: IncomingMessage | undefined
): import('@sentry/core').Span | undefined {
  if (!req) return undefined
  return (req as IncomingMessage & { __sentrySpan?: import('@sentry/core').Span }).__sentrySpan
}

export function createPerformanceLink(req?: IncomingMessage): ApolloLink {
  return new ApolloLink((operation: Operation, forward: NextLink): Observable<FetchResult> => {
    // Skip on client-side
    if (!process.server) {
      return forward(operation)
    }

    // Skip if feature is disabled
    if (!isEnabled() || !Sentry) {
      return forward(operation)
    }

    // Get parent span from request object (stored by ssrPerformance middleware)
    const parentSpan = getSpanFromRequest(req)
    if (!parentSpan) {
      return forward(operation)
    }

    const operationName = operation.operationName || 'UnnamedOperation'
    const operationType = getOperationType(operation)

    return new Observable<FetchResult>((observer) => {
      let subscription: ZenObservable.Subscription | null = null
      let span: import('@sentry/core').Span | undefined

      try {
        // Sentry v10: Use startInactiveSpan with explicit parentSpan for proper waterfall
        span = Sentry!.startInactiveSpan({
          name: operationName,
          op: `graphql.${operationType}`,
          parentSpan: parentSpan,
          attributes: {
            'graphql.operation.name': operationName,
            'graphql.operation.type': operationType,
            'graphql.variables': JSON.stringify(sanitizeVariables(operation.variables)).substring(
              0,
              1024
            )
          }
        })

        subscription = forward(operation).subscribe({
          next: (result) => {
            if (span) {
              if (result.errors?.length) {
                span.setAttribute(
                  'graphql.errors',
                  JSON.stringify(result.errors.map((e) => e.message))
                )
                span.setStatus({ code: 2 })
              } else {
                span.setStatus({ code: 1 })
              }
            }
            observer.next(result)
          },
          error: (error) => {
            if (span) {
              span.setAttribute('graphql.error', error.message || 'Unknown error')
              span.setStatus({ code: 2 })
              span.end()
            }
            observer.error(error)
          },
          complete: () => {
            if (span) {
              span.end()
            }
            observer.complete()
          }
        })
      } catch (_e) {
        // If span creation fails, just forward without tracing
        if (span) {
          span.end()
        }
        subscription = forward(operation).subscribe(observer)
      }

      return () => {
        if (span) {
          span.setStatus({ code: 1 })
          span.end()
        }
        subscription?.unsubscribe()
      }
    })
  })
}

export default createPerformanceLink
